import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, 
  FileText, 
  Users, 
  Save, 
  Plus, 
  Pencil, 
  Trash2,
  UserPlus,
  Shield,
  Loader2,
  Upload,
  Image,
  Mail,
  Key,
  AlertTriangle,
  XCircle,
  Search
} from 'lucide-react';
import { SortableHeader } from '@/components/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'FOREMAN', label: 'Foreman', description: 'Field crew - creates timesheets & POs' },
  { value: 'OFFICE', label: 'Office', description: 'Admin - full access, manages everything' },
  { value: 'OWNER', label: 'Owner', description: 'Full access + change order approval' },
];

export default function Settings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Company Profile
  const [company, setCompany] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    logoUrl: '',
  });
  
  // Settings
  const [settings, setSettings] = useState({
    defaultMarkupPct: 15,
    defaultTaxRate: 0,
    invoiceNumberPrefix: 'INV',
    invoicePaymentInstructions: '',
    invoiceFooterNotes: '',
    poNumberPrefix: 'MPO',
    sendgridApiKey: '',
    senderEmail: '',
  });
  
  // User Management
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userSortCol, setUserSortCol] = useState('name');
  const [userSortDir, setUserSortDir] = useState('asc');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'FOREMAN',
    password: '',
  });
  const [userSubmitting, setUserSubmitting] = useState(false);
  
  // Cancellation
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  
  // QuickBooks Integration
  const [qboStatus, setQboStatus] = useState(null);
  const [qboLoading, setQboLoading] = useState(false);
  const [qboDisconnectDialog, setQboDisconnectDialog] = useState(false);

  useEffect(() => {
    fetchData();
    // Fetch QBO status separately so it doesn't block settings load
    fetchQboStatus().catch(() => {});
    
    // Check for QBO callback status in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('qbo_success') === 'true') {
      toast.success('Successfully connected to QuickBooks Online!');
      fetchQboStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('qbo_error')) {
      const error = urlParams.get('qbo_error').replace(/_/g, ' ');
      toast.error(`QuickBooks connection failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [companyRes, settingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/company`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      setCompany({
        name: companyRes.data.name || '',
        address: companyRes.data.address || '',
        phone: companyRes.data.phone || '',
        email: companyRes.data.email || '',
        logoUrl: companyRes.data.logoUrl || '',
      });
      
      setSettings({
        defaultMarkupPct: settingsRes.data.defaultMarkupPct || 15,
        defaultTaxRate: settingsRes.data.defaultTaxRate || 0,
        invoiceNumberPrefix: settingsRes.data.invoiceNumberPrefix || 'INV',
        invoicePaymentInstructions: settingsRes.data.invoicePaymentInstructions || '',
        invoiceFooterNotes: settingsRes.data.invoiceFooterNotes || '',
        poNumberPrefix: settingsRes.data.poNumberPrefix || 'MPO',
        sendgridApiKey: settingsRes.data.sendgridApiKey || '',
        senderEmail: settingsRes.data.senderEmail || '',
      });
      
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchQboStatus = async () => {
    try {
      const response = await axios.get(`${API}/qbo/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQboStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch QBO status:', error);
      setQboStatus({ configured: false, connected: false });
    }
  };

  const handleQboConnect = async () => {
    setQboLoading(true);
    try {
      const response = await axios.get(`${API}/qbo/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Intuit authorization page
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Failed to initiate QBO connection:', error);
      toast.error('Failed to connect to QuickBooks');
      setQboLoading(false);
    }
  };

  const handleQboDisconnect = async () => {
    setQboLoading(true);
    try {
      await axios.post(`${API}/qbo/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Disconnected from QuickBooks Online');
      setQboDisconnectDialog(false);
      fetchQboStatus();
    } catch (error) {
      console.error('Failed to disconnect QBO:', error);
      toast.error('Failed to disconnect from QuickBooks');
    } finally {
      setQboLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/company`, company, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Company profile saved');
    } catch (error) {
      console.error('Failed to save company:', error);
      toast.error('Failed to save company profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be smaller than 2MB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      setCompany({ ...company, logoUrl: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleOpenUserDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        name: userToEdit.name,
        email: userToEdit.email,
        role: userToEdit.role,
        password: '',
      });
    } else {
      setEditingUser(null);
      setUserForm({
        name: '',
        email: '',
        role: 'FOREMAN',
        password: '',
      });
    }
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error('Name and email are required');
      return;
    }
    
    if (!editingUser && !userForm.password) {
      toast.error('Password is required for new users');
      return;
    }
    
    setUserSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        await axios.patch(`${API}/users/${editingUser.id}`, {
          name: userForm.name,
          role: userForm.role,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('User updated');
      } else {
        // Create new user
        await axios.post(`${API}/users`, userForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('User created');
      }
      
      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleToggleUserActive = async (userToToggle) => {
    try {
      await axios.patch(`${API}/users/${userToToggle.id}`, {
        active: !userToToggle.active,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(userToToggle.active ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      await axios.delete(`${API}/users/${deletingUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User deactivated');
      setDeleteUserDialogOpen(false);
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleTransferOwnership = async (targetUserId) => {
    if (!window.confirm('Are you sure you want to transfer ownership to this user? You will become an Office user.')) {
      return;
    }
    
    try {
      await axios.patch(`${API}/users/${targetUserId}/role`, { role: 'OWNER' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Ownership transferred. You are now an Office user. Please log in again.');
      // Force re-login to get new role
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer ownership');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setUserSubmitting(true);
    try {
      await axios.post(`${API}/auth/admin-reset-password`, {
        userId: resetPasswordUser.id,
        newPassword: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleRequestCancellation = async () => {
    setCancelSubmitting(true);
    try {
      const response = await axios.post(`${API}/subscription/request-cancellation`, {
        reason: cancelReason
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      setCancelDialogOpen(false);
      setCancelReason('');
    } catch (error) {
      console.error('Failed to submit cancellation request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit cancellation request');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleUserSort = (column) => {
    if (userSortCol === column) setUserSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setUserSortCol(column); setUserSortDir('asc'); }
  };

  const sortedUsers = useMemo(() => {
    let result = [...users];
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      result = result.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q));
    }
    if (userSortCol) {
      result.sort((a, b) => {
        const aVal = a[userSortCol] || '';
        const bVal = b[userSortCol] || '';
        return userSortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
      });
    }
    return result;
  }, [users, userSearch, userSortCol, userSortDir]);

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Settings</h1>
        <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">Manage your company profile, invoice settings, and users</p>
      </div>

      <Tabs defaultValue="company" className="space-y-4 md:space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-sm flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="company" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
            <Building2 className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Company</span>
            <span className="sm:hidden">Co.</span>
          </TabsTrigger>
          <TabsTrigger value="invoice" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
            <FileText className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Invoice</span>
            <span className="sm:hidden">Inv.</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
            <Mail className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
            <Users className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="subscription" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
            <Shield className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Subscription</span>
            <span className="sm:hidden">Sub.</span>
          </TabsTrigger>
          {(user?.role === 'OWNER' || user?.role === 'OFFICE') && (
            <TabsTrigger value="integrations" className="data-[state=active]:bg-white rounded-sm text-xs md:text-sm py-2 px-2 md:px-3">
              <Key className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Integrations</span>
              <span className="sm:hidden">Int.</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Company Profile Tab */}
        <TabsContent value="company">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#1A3668]" />
                Company Profile
              </CardTitle>
              <CardDescription>
                Your company information appears on invoices and reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Company Logo
                </Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden">
                    {company.logoUrl ? (
                      <img 
                        src={company.logoUrl} 
                        alt="Logo" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload').click()}
                      className="rounded-sm"
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="companyName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Company Name
                  </Label>
                  <Input
                    id="companyName"
                    value={company.name}
                    onChange={(e) => setCompany({ ...company, name: e.target.value })}
                    placeholder="Your Company Name"
                    className="mt-2 rounded-sm border-2"
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="companyEmail" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Email
                  </Label>
                  <Input
                    id="companyEmail"
                    type="email"
                    value={company.email}
                    onChange={(e) => setCompany({ ...company, email: e.target.value })}
                    placeholder="contact@company.com"
                    className="mt-2 rounded-sm border-2"
                    data-testid="company-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="companyPhone" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Phone
                  </Label>
                  <Input
                    id="companyPhone"
                    value={company.phone}
                    onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-2 rounded-sm border-2"
                    data-testid="company-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="companyAddress" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Address
                </Label>
                <Textarea
                  id="companyAddress"
                  value={company.address}
                  onChange={(e) => setCompany({ ...company, address: e.target.value })}
                  placeholder="123 Main Street&#10;City, State 12345"
                  rows={3}
                  className="mt-2 rounded-sm border-2"
                  data-testid="company-address-input"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveCompany}
                  disabled={saving}
                  className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
                  data-testid="save-company-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Company Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Settings Tab */}
        <TabsContent value="invoice">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1A3668]" />
                Invoice Settings
              </CardTitle>
              <CardDescription>
                Default values for new invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="defaultTaxRate" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Default Tax Rate (%)
                  </Label>
                  <Input
                    id="defaultTaxRate"
                    type="number"
                    value={settings.defaultTaxRate}
                    onChange={(e) => setSettings({ ...settings, defaultTaxRate: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                    step="0.1"
                    className="mt-2 rounded-sm border-2"
                    data-testid="default-tax-rate-input"
                  />
                </div>

                <div>
                  <Label htmlFor="invoicePrefix" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Invoice Number Prefix
                  </Label>
                  <Input
                    id="invoicePrefix"
                    value={settings.invoiceNumberPrefix}
                    onChange={(e) => setSettings({ ...settings, invoiceNumberPrefix: e.target.value.toUpperCase() })}
                    placeholder="INV"
                    maxLength={10}
                    className="mt-2 rounded-sm border-2 font-mono"
                    data-testid="invoice-prefix-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">e.g., INV-26-0001</p>
                </div>

                <div>
                  <Label htmlFor="defaultMarkup" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Default Markup (%)
                  </Label>
                  <Input
                    id="defaultMarkup"
                    type="number"
                    value={settings.defaultMarkupPct}
                    onChange={(e) => setSettings({ ...settings, defaultMarkupPct: parseFloat(e.target.value) || 0 })}
                    placeholder="15"
                    min="0"
                    step="0.5"
                    className="mt-2 rounded-sm border-2"
                    data-testid="default-markup-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="paymentInstructions" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Payment Instructions
                </Label>
                <Textarea
                  id="paymentInstructions"
                  value={settings.invoicePaymentInstructions}
                  onChange={(e) => setSettings({ ...settings, invoicePaymentInstructions: e.target.value })}
                  placeholder="Make checks payable to: Your Company Name&#10;Mail to: 123 Main St, City, State 12345"
                  rows={3}
                  className="mt-2 rounded-sm border-2"
                  data-testid="payment-instructions-input"
                />
              </div>

              <div>
                <Label htmlFor="footerNotes" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Invoice Footer Notes
                </Label>
                <Textarea
                  id="footerNotes"
                  value={settings.invoiceFooterNotes}
                  onChange={(e) => setSettings({ ...settings, invoiceFooterNotes: e.target.value })}
                  placeholder="Thank you for your business! Late payments may be subject to a 1.5% monthly fee."
                  rows={2}
                  className="mt-2 rounded-sm border-2"
                  data-testid="footer-notes-input"
                />
              </div>

              <div className="pt-4 border-t border-slate-200">
                <Label htmlFor="poPrefix" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  PO Number Prefix
                </Label>
                <Input
                  id="poPrefix"
                  value={settings.poNumberPrefix}
                  onChange={(e) => setSettings({ ...settings, poNumberPrefix: e.target.value.toUpperCase() })}
                  placeholder="MPO"
                  maxLength={10}
                  className="mt-2 w-40 rounded-sm border-2 font-mono"
                  data-testid="po-prefix-input"
                />
                <p className="text-xs text-slate-500 mt-1">Default prefix for Material POs (can be overridden per customer)</p>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Configuration Tab */}
        <TabsContent value="email">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#1A3668]" />
                Email Configuration
              </CardTitle>
              <CardDescription>
                Configure SendGrid to send invoices directly to customers via email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                <h4 className="font-bold text-blue-800 mb-2">How to Get a SendGrid API Key</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">sendgrid.com</a> and create a free account</li>
                  <li>Navigate to Settings → API Keys</li>
                  <li>Create a new API key with "Mail Send" permissions</li>
                  <li>Copy the key and paste it below</li>
                  <li>Verify your sender email address in SendGrid</li>
                </ol>
              </div>

              <div>
                <Label htmlFor="sendgridApiKey" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  SendGrid API Key
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Key className="w-4 h-4 text-slate-400" />
                  <Input
                    id="sendgridApiKey"
                    type="password"
                    value={settings.sendgridApiKey || ''}
                    onChange={(e) => setSettings({ ...settings, sendgridApiKey: e.target.value })}
                    placeholder="SG.xxxxxxxx..."
                    className="rounded-sm border-2 font-mono"
                    data-testid="sendgrid-api-key-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Your API key is stored securely and never displayed after saving</p>
              </div>

              <div>
                <Label htmlFor="senderEmail" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Sender Email Address
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <Input
                    id="senderEmail"
                    type="email"
                    value={settings.senderEmail || ''}
                    onChange={(e) => setSettings({ ...settings, senderEmail: e.target.value })}
                    placeholder="invoices@yourcompany.com"
                    className="rounded-sm border-2"
                    data-testid="sender-email-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">This email must be verified in your SendGrid account</p>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
                  data-testid="save-email-settings-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Email Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Management Tab */}
        <TabsContent value="users">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1A3668]" />
                  User Management
                </CardTitle>
                <CardDescription>
                  Manage team members and their access levels
                </CardDescription>
              </div>
              <Button
                onClick={() => handleOpenUserDialog()}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm w-full sm:w-auto py-3 sm:py-2"
                data-testid="add-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </CardHeader>
            <CardContent>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {users.map((u) => (
                  <Card key={u.id} data-testid={`user-card-${u.id}`} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-600">{u.email}</p>
                        </div>
                        <Badge 
                          className={`rounded-sm text-xs ${u.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                        >
                          {u.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <Badge 
                        className={`rounded-sm mb-3 ${
                          u.role === 'OFFICE' ? 'bg-blue-100 text-blue-700' :
                          u.role === 'OWNER' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}
                      >
                        <Shield className="w-3 h-3 mr-1" />
                        {ROLE_OPTIONS.find(r => r.value === u.role)?.label || u.role}
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenUserDialog(u)}
                          disabled={u.id === user?.id}
                          className="flex-1 rounded-sm"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setResetPasswordUser(u);
                            setNewPassword('');
                            setResetPasswordDialogOpen(true);
                          }}
                          disabled={u.id === user?.id}
                          className="text-blue-600 rounded-sm"
                          data-testid={`reset-password-${u.id}`}
                        >
                          <Key className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleUserActive(u)}
                          disabled={u.id === user?.id}
                          className={`rounded-sm ${u.active !== false ? 'text-amber-600' : 'text-green-600'}`}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          {u.active !== false ? 'Deact.' : 'Act.'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* User Search */}
              <div className='mb-4'>
                <div className='relative max-w-xs'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                  <Input data-testid='user-search' value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                    placeholder='Search users...' className='pl-9 rounded-sm border-2' />
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table data-testid='users-table'>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <SortableHeader column='name' label='Name' sortColumn={userSortCol} sortDir={userSortDir} onSort={handleUserSort} />
                      <SortableHeader column='email' label='Email' sortColumn={userSortCol} sortDir={userSortDir} onSort={handleUserSort} />
                      <SortableHeader column='role' label='Role' sortColumn={userSortCol} sortDir={userSortDir} onSort={handleUserSort} />
                      <SortableHeader column='active' label='Status' sortColumn={userSortCol} sortDir={userSortDir} onSort={handleUserSort} />
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Actions</th>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge 
                            className={`rounded-sm ${
                              u.role === 'OFFICE' ? 'bg-blue-100 text-blue-700' :
                              u.role === 'OWNER' ? 'bg-purple-100 text-purple-700' :
                              'bg-green-100 text-green-700'
                            }`}
                          >
                            <Shield className="w-3 h-3 mr-1" />
                            {ROLE_OPTIONS.find(r => r.value === u.role)?.label || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={`rounded-sm ${u.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                          >
                            {u.active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenUserDialog(u)}
                              disabled={u.id === user?.id}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setResetPasswordUser(u);
                                setNewPassword('');
                                setResetPasswordDialogOpen(true);
                              }}
                              disabled={u.id === user?.id}
                              className="text-blue-600"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {/* Transfer Ownership button - only shown to Owner for non-Owner users */}
                            {user?.role === 'OWNER' && u.role !== 'OWNER' && u.id !== user?.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleTransferOwnership(u.id)}
                                className="text-purple-600"
                                data-testid={`transfer-ownership-${u.id}`}
                                title="Transfer Ownership"
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleUserActive(u)}
                              disabled={u.id === user?.id}
                              className={u.active !== false ? 'text-amber-600' : 'text-green-600'}
                              data-testid={`toggle-user-${u.id}`}
                            >
                              {u.active !== false ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1A3668]" />
                Subscription
              </CardTitle>
              <CardDescription>
                Manage your BSO SiteTrack subscription
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Plan Info */}
              <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-2">Current Plan</h3>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#1A3668] text-white rounded-sm">
                    {company.plan || 'TRIAL'}
                  </Badge>
                  <Badge variant="outline" className="rounded-sm">
                    {company.subscriptionStatus || 'ACTIVE'}
                  </Badge>
                </div>
                {company.plan === 'CORE' && (
                  <p className="text-sm text-slate-600 mt-2">
                    BSO SiteTrack Core - $75/month base + per-seat pricing
                  </p>
                )}
              </div>

              {/* Contact Support */}
              <div className="bg-blue-50 p-4 rounded-sm border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-2">Need Help?</h3>
                <p className="text-sm text-blue-700">
                  For billing questions, plan upgrades, or support, contact us at{' '}
                  <a href="mailto:meriah@bsofinancialservices.com" className="font-bold underline">
                    meriah@bsofinancialservices.com
                  </a>
                </p>
              </div>

              {/* Cancel Subscription */}
              <div className="bg-red-50 p-4 rounded-sm border border-red-200">
                <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Cancel Subscription
                </h3>
                <p className="text-sm text-red-700 mb-4">
                  If you need to cancel your subscription, click below to send us a cancellation request. 
                  We'll process it and stop any future invoicing.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setCancelDialogOpen(true)}
                  className="border-red-300 text-red-700 hover:bg-red-100 rounded-sm"
                  data-testid="request-cancellation-button"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Request Cancellation
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <Card className="border-slate-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#1A3668]" />
                Integrations
              </CardTitle>
              <CardDescription>
                Connect external services to automate your workflow
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* QuickBooks Online */}
              <div className="border border-slate-200 rounded-sm p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-8 h-8 text-green-600" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2zm4 8h-2v-4h2v4zm0-6h-2V9h2v2z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-800">QuickBooks Online</h3>
                      <p className="text-sm text-slate-600">
                        Sync bills and expenses automatically from QuickBooks
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {qboStatus?.connected ? (
                      <Badge className="bg-green-100 text-green-800 rounded-sm">Connected</Badge>
                    ) : qboStatus?.configured ? (
                      <Badge className="bg-amber-100 text-amber-800 rounded-sm">Not Connected</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600 rounded-sm">Not Configured</Badge>
                    )}
                  </div>
                </div>

                {qboStatus?.configured && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    {qboStatus?.connected ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700 font-medium">
                            ✓ Connected to QuickBooks Online
                          </p>
                          {qboStatus?.realmId && (
                            <p className="text-xs text-slate-500 mt-1">
                              Realm ID: {qboStatus.realmId}
                            </p>
                          )}
                        </div>
                        {user?.role === 'OWNER' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setQboDisconnectDialog(true)}
                            className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                            data-testid="qbo-disconnect-btn"
                          >
                            Disconnect
                          </Button>
                        )}
                      </div>
                    ) : qboStatus?.status === 'RECONNECT_REQUIRED' ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-700 font-medium flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Reconnection required
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {qboStatus?.message || 'Please reconnect to continue syncing'}
                          </p>
                        </div>
                        {user?.role === 'OWNER' && (
                          <Button
                            onClick={handleQboConnect}
                            disabled={qboLoading}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                            data-testid="qbo-reconnect-btn"
                          >
                            {qboLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Reconnect
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600">
                          Connect your QuickBooks Online account to automatically sync bills and expenses.
                        </p>
                        {user?.role === 'OWNER' && (
                          <Button
                            onClick={handleQboConnect}
                            disabled={qboLoading}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                            data-testid="qbo-connect-btn"
                          >
                            {qboLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Connect to QuickBooks
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!qboStatus?.configured && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      QuickBooks integration is not configured for this environment. 
                      Contact support to enable this feature.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* QBO Disconnect Dialog */}
      <AlertDialog open={qboDisconnectDialog} onOpenChange={setQboDisconnectDialog}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-800 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Disconnect QuickBooks?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect your QuickBooks Online account. You can reconnect at any time.
              <br /><br />
              Previously imported data will remain in the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleQboDisconnect}
              disabled={qboLoading}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
              data-testid="qbo-confirm-disconnect-btn"
            >
              {qboLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user details and role' : 'Invite a new team member'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="userName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Full Name *
              </Label>
              <Input
                id="userName"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="John Smith"
                className="mt-2 rounded-sm border-2"
                data-testid="user-name-input"
              />
            </div>

            <div>
              <Label htmlFor="userEmail" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Email *
              </Label>
              <Input
                id="userEmail"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="john@company.com"
                disabled={!!editingUser}
                className="mt-2 rounded-sm border-2"
                data-testid="user-email-input"
              />
            </div>

            <div>
              <Label htmlFor="userRole" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Role *
              </Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-slate-500 ml-2">- {role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!editingUser && (
              <div>
                <Label htmlFor="userPassword" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Initial Password *
                </Label>
                <Input
                  id="userPassword"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-2 rounded-sm border-2"
                  data-testid="user-password-input"
                />
                <p className="text-xs text-slate-500 mt-1">User can change this after first login</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUserDialogOpen(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={userSubmitting}
              className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
              data-testid="save-user-btn"
            >
              {userSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{deletingUser?.name}</strong>? They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetPasswordUser?.name}</strong> ({resetPasswordUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="newPassword" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="mt-2 rounded-sm border-2"
                data-testid="admin-reset-password-input"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setResetPasswordUser(null);
                setNewPassword('');
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={userSubmitting || newPassword.length < 6}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
              data-testid="admin-reset-password-submit"
            >
              {userSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Request Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Request Cancellation
            </DialogTitle>
            <DialogDescription>
              We're sorry to see you go. Please let us know why you're cancelling so we can improve.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cancelReason" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Reason for Cancellation (Optional)
              </Label>
              <Textarea
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please share why you're cancelling..."
                className="mt-2 rounded-sm border-2 min-h-[100px]"
                data-testid="cancel-reason-input"
              />
            </div>
            
            <div className="bg-amber-50 p-3 rounded-sm border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Your account will remain active until we process your request. 
                We'll contact you to confirm the cancellation.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setCancelReason('');
              }}
              className="rounded-sm"
            >
              Keep Subscription
            </Button>
            <Button
              onClick={handleRequestCancellation}
              disabled={cancelSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="confirm-cancellation-button"
            >
              {cancelSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Submit Cancellation Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
